package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.data.AdminRepo
import org.gymly.model.AdminSettingsModules
import org.gymly.model.AdminSettingsPermissionModule
import org.gymly.model.AdminUser
import org.gymly.model.isPlatform
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace

private enum class SettingsTab(val label: String) {
    License("Licencia"),
    Company("Empresa"),
    Categories("Categorías"),
    Users("Usuarios"),
    Roles("Roles y permisos"),
    Approvals("Aprobaciones"),
    Mail("Correo"),
    WhatsApp("WhatsApp"),
    PaymentMethods("Tipos de pago"),
    Exchange("Cotización"),
    Payments("Pasarelas"),
    Access("Acceso"),
    Notifications("Notificaciones"),
    Agents("Agentes"),
    Audit("Historial"),
    ;

    fun requiredPermission(): AdminSettingsPermissionModule? = when (this) {
        License -> null
        Exchange, PaymentMethods, Notifications -> AdminSettingsPermissionModule.CONFIG_OPS_VIEW
        Company, Categories, Mail, WhatsApp, Payments, Access -> AdminSettingsPermissionModule.CONFIG_BIZ_VIEW
        Users, Roles, Approvals, Audit, Agents -> AdminSettingsPermissionModule.CONFIG_SEC_VIEW
    }

    fun requiredEditPermission(): AdminSettingsPermissionModule? = when (this) {
        License -> null
        Exchange, PaymentMethods, Notifications -> AdminSettingsPermissionModule.CONFIG_OPS_EDIT
        Company, Categories, Mail, WhatsApp, Payments, Access -> AdminSettingsPermissionModule.CONFIG_BIZ_EDIT
        Users, Roles, Approvals, Audit, Agents -> AdminSettingsPermissionModule.CONFIG_SEC_EDIT
    }

    fun isAllowed(permissions: Set<AdminSettingsPermissionModule>, platformUser: Boolean): Boolean =
        when (this) {
            License -> true
            // Misma regla que el backend (canApproveAnything): cualquier permiso *_APPROVE
            Approvals -> permissions.any { it.name.endsWith("_APPROVE") }
            else -> {
                if (platformUser) false
                else {
                    val view = requiredPermission()
                    val edit = requiredEditPermission()
                    (view != null && permissions.contains(view)) || (edit != null && permissions.contains(edit))
                }
            }
        }
}

private enum class SettingsDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

@Composable
fun AdminSettingsScreen(
    user: AdminUser,
    pendingApprovalsCount: Long = 0L,
    openApprovalsTab: Boolean = false,
) {
    val platformUser = user.isPlatform()
    val visibleTabs = when {
        platformUser -> listOf(SettingsTab.License)
        !user.permissionsKnown -> SettingsTab.entries.toList()
        else -> SettingsTab.entries.filter { it.isAllowed(user.permissions, platformUser = false) }
    }
    var tab by remember(openApprovalsTab, visibleTabs, platformUser) {
        mutableStateOf(
            when {
                platformUser && SettingsTab.License in visibleTabs -> SettingsTab.License
                openApprovalsTab && SettingsTab.Approvals in visibleTabs -> SettingsTab.Approvals
                pendingApprovalsCount > 0 && SettingsTab.Approvals in visibleTabs -> SettingsTab.Approvals
                visibleTabs.isNotEmpty() -> visibleTabs.first()
                else -> SettingsTab.Company
            },
        )
    }
    var dateFilter by remember { mutableStateOf(SettingsDateFilter.All) }
    var modules by remember { mutableStateOf(AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true)) }

    LaunchedEffect(Unit) {
        modules = runCatching { AdminRepo.api.getSettingsModules() }
            .getOrDefault(AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true))
    }

    LaunchedEffect(user.permissionsKnown, visibleTabs) {
        if (user.permissionsKnown && visibleTabs.isNotEmpty() && tab !in visibleTabs) {
            tab = visibleTabs.first()
        }
    }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        if (user.permissionsKnown && visibleTabs.isEmpty()) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text(
                    "No tienes permiso para ver configuración.",
                    color = AdminColors.TextMuted,
                    fontSize = 14.sp,
                )
            }
            return@BoxWithConstraints
        }
        // Celular: todo Configuración (encabezado, pestañas y contenido) se desplaza junto.
        Column(Modifier.fillMaxSize().then(if (compact) Modifier.verticalScroll(rememberScrollState()) else Modifier)) {
            SettingsHeader(
                showDateFilter = tab == SettingsTab.Users || tab == SettingsTab.Audit,
                dateFilter = dateFilter,
                onFilter = { dateFilter = it },
            )
            Spacer(Modifier.height(12.dp))
            if (compact) {
                SettingsTabs(visibleTabs, tab, pendingApprovalsCount, compact) { tab = it }
                Spacer(Modifier.height(16.dp))
            }
            Row(Modifier.weight(1f).fillMaxWidth()) {
            if (!compact) {
                SettingsSideNav(visibleTabs, tab, pendingApprovalsCount) { tab = it }
                Spacer(Modifier.width(20.dp))
            }
            Column(Modifier.weight(1f).fillMaxSize()) {
                when (tab) {
                SettingsTab.License -> SettingsLicensePanel(
                    canActivate = platformUser,
                    compact = compact,
                )
                SettingsTab.Company -> SettingsCompanyPanel(
                    compact = compact,
                    modules = modules,
                    onModulesChange = { modules = it },
                )
                SettingsTab.Categories -> AdminProductCategoriesPanel()
                SettingsTab.Users -> SettingsUsersPanel(dateFilter.days, compact)
                SettingsTab.Roles -> SettingsRolesPanel(compact)
                SettingsTab.Approvals -> AdminApprovalsPanel(user = user)
                SettingsTab.Mail -> SettingsMailPanel(compact)
                SettingsTab.WhatsApp -> SettingsWhatsAppPanel(compact)
                SettingsTab.PaymentMethods -> SettingsPaymentMethodsPanel(compact)
                SettingsTab.Exchange -> SettingsExchangePanel(
                    compact = compact,
                    modules = modules,
                    onModulesChange = { modules = it },
                )
                SettingsTab.Payments -> SettingsPaymentsPanel(compact)
                SettingsTab.Access -> SettingsAccessPanel(
                    compact = compact,
                    modules = modules,
                    onModulesChange = { modules = it },
                )
                SettingsTab.Notifications -> SettingsNotificationsPanel(compact)
                SettingsTab.Agents -> SettingsAgentsPanel(compact)
                SettingsTab.Audit -> SettingsAuditPanel(dateFilter.days, compact)
                }
            }
            }
        }
    }
}

@Composable
private fun SettingsHeader(
    showDateFilter: Boolean,
    dateFilter: SettingsDateFilter,
    onFilter: (SettingsDateFilter) -> Unit,
) {
    var menu by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Configuración", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Empresa, categorías de productos, roles, correo, WhatsApp, tipos de pago, cotización BCV, pasarelas, acceso, notificaciones e historial.",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        if (showDateFilter) {
            Box {
                OutlinedButton(onClick = { menu = true }, shape = RoundedCornerShape(10.dp)) {
                    Text(dateFilter.label, color = AdminColors.Text)
                }
                DropdownMenu(expanded = menu, onDismissRequest = { menu = false }) {
                    SettingsDateFilter.entries.forEach { opt ->
                        DropdownMenuItem(
                            text = { Text(opt.label) },
                            onClick = {
                                onFilter(opt)
                                menu = false
                            },
                        )
                    }
                }
            }
        }
    }
}

/** Orden de las opciones en el menú lateral de Configuración en escritorio (sin encabezados de grupo). */
private val SETTINGS_SIDE_ORDER: List<SettingsTab> = listOf(
    SettingsTab.License,
    SettingsTab.Company,
    SettingsTab.Categories,
    SettingsTab.PaymentMethods,
    SettingsTab.Exchange,
    SettingsTab.Payments,
    SettingsTab.Users,
    SettingsTab.Roles,
    SettingsTab.Approvals,
    SettingsTab.Access,
    SettingsTab.Mail,
    SettingsTab.WhatsApp,
    SettingsTab.Notifications,
    SettingsTab.Agents,
    SettingsTab.Audit,
)

@Composable
private fun SettingsSideNav(
    tabs: List<SettingsTab>,
    current: SettingsTab,
    pendingApprovalsCount: Long,
    onSelect: (SettingsTab) -> Unit,
) {
    Column(
        Modifier.width(220.dp).fillMaxHeight().verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        SETTINGS_SIDE_ORDER.filter { it in tabs }.forEach { t ->
            val selected = t == current
            val label = if (t == SettingsTab.Approvals && pendingApprovalsCount > 0) {
                "${t.label} ($pendingApprovalsCount)"
            } else {
                t.label
            }
            Text(
                label,
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(
                        if (selected) AdminColors.Primary.copy(alpha = 0.18f)
                        else AdminColors.Primary.copy(alpha = 0.07f),
                    )
                    .clickable { onSelect(t) }
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                color = if (selected) AdminColors.Primary else AdminColors.Text,
                fontWeight = if (selected) FontWeight.Bold else FontWeight.Medium,
                fontSize = 13.sp,
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SettingsTabs(
    tabs: List<SettingsTab>,
    current: SettingsTab,
    pendingApprovalsCount: Long,
    compact: Boolean,
    onSelect: (SettingsTab) -> Unit,
) {
    val tabItem: @Composable (SettingsTab) -> Unit = { t ->
        val selected = t == current
        val label = if (t == SettingsTab.Approvals && pendingApprovalsCount > 0) {
            "${t.label} ($pendingApprovalsCount)"
        } else {
            t.label
        }
        Text(
            label,
            modifier = Modifier
                .clip(RoundedCornerShape(10.dp))
                .background(if (selected) AdminColors.Primary else AdminColors.SurfaceContainer)
                .clickable { onSelect(t) }
                .padding(horizontal = 14.dp, vertical = 10.dp),
            color = if (selected) AdminColors.OnBrand else AdminColors.Text,
            fontWeight = FontWeight.SemiBold,
            fontSize = 13.sp,
        )
    }
    if (compact) {
        // Celular: las pestañas se acomodan en varias filas para verse todas, sin deslizar.
        FlowRow(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            tabs.forEach { tabItem(it) }
        }
    }
}
